import type { TikTokPresets, TikTokPrivacy } from "@shared/schema";

// TikTok legal links required in the compliance declaration (Content Sharing Guidelines).
export const TIKTOK_MUSIC_USAGE_URL =
  "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
export const TIKTOK_BRANDED_CONTENT_POLICY_URL =
  "https://www.tiktok.com/legal/page/global/bc-policy/en";

// i18n key per raw privacy_level value returned by creator_info.
export const TIKTOK_PRIVACY_LABEL_KEY: Record<Exclude<TikTokPrivacy, "">, string> = {
  PUBLIC_TO_EVERYONE: "contentScheduler.presets.tiktok.privacyPublic",
  MUTUAL_FOLLOW_FRIENDS: "contentScheduler.presets.tiktok.privacyFriends",
  FOLLOWER_OF_CREATOR: "contentScheduler.presets.tiktok.privacyFollowers",
  SELF_ONLY: "contentScheduler.presets.tiktok.privacyPrivate",
};

export type TikTokValidityReason =
  | "privacy_not_selected"
  | "disclosure_without_subtype"
  | "branded_content_private_conflict"
  | null;

/**
 * Validates the TikTok presets against the Content Sharing Guidelines rules that
 * depend only on the user's choices (not on live creator_info). Used both for the
 * inline composer warnings and to gate the Post button in the parent scheduler:
 *  - a privacy level must be chosen (no default allowed),
 *  - if the commercial-disclosure toggle is on, at least one of Your Brand /
 *    Branded Content must be selected,
 *  - Branded Content cannot be combined with a private (SELF_ONLY) post.
 */
export function getTikTokValidity(presets: TikTokPresets): TikTokValidityReason {
  if (!presets.privacy) return "privacy_not_selected";
  if (presets.commercialDisclosure && !presets.brandOrganic && !presets.brandContent) {
    return "disclosure_without_subtype";
  }
  if (presets.brandContent && presets.privacy === "SELF_ONLY") {
    return "branded_content_private_conflict";
  }
  return null;
}

export function isTikTokPresetsValid(presets: TikTokPresets): boolean {
  return getTikTokValidity(presets) === null;
}
